import {
  ChecklistIcon,
  CopyIcon,
  EyeClosedIcon,
  FileIcon,
  KebabHorizontalIcon,
  LinkExternalIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useState} from 'react'

import styles from './ArtifactRow.module.css'
import type {DerivedArtifact, IssueArtifact} from './types'

function ArtifactIcon({type}: {type: IssueArtifact['type']}) {
  if (type === 'plan') return <ChecklistIcon size={14} aria-label="Plan" />
  if (type === 'link') return <LinkExternalIcon size={14} aria-label="Link" />
  return <FileIcon size={14} aria-label="File" />
}

type PersistedArtifactRowProps = {
  artifact: IssueArtifact
  onSelect?: (artifact: IssueArtifact) => void
  onDelete?: (id: number) => void
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl({
  artifact,
  onSelect,
  onDelete,
}: PersistedArtifactRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  const handleDelete = useCallback(() => {
    onDelete?.(artifact.id)
  }, [artifact.id, onDelete])

  const handleSelect = useCallback(() => {
    onSelect?.(artifact)
  }, [artifact, onSelect])

  const description = artifact.body?.trim()
  const isLink = artifact.type === 'link'

  const content = (
    <>
      <span className={styles.Icon}>
        <ArtifactIcon type={artifact.type} />
      </span>
      <div className={styles.Content}>
        {isLink ? (
          <Link className={styles.TitleLink} href={artifact.body} target="_blank" rel="noopener noreferrer">
            {artifact.name}
          </Link>
        ) : (
          <span className={styles.Title}>{artifact.name}</span>
        )}
        {!isLink && description && <span className={styles.Description}>{description}</span>}
      </div>
    </>
  )

  return (
    <div className={clsx(styles.Row, menuOpen && styles.menuOpen)}>
      {onSelect && !isLink ? (
        <button type="button" className={styles.RowButton} aria-label={artifact.name} onClick={handleSelect}>
          {content}
        </button>
      ) : (
        <div className={styles.RowContent}>{content}</div>
      )}
      <div className={styles.HoverActions}>
        <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              size="small"
              aria-label="More actions"
              onClick={e => e.stopPropagation()}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item variant="danger" onSelect={handleDelete}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
})

type DerivedArtifactRowProps = {
  artifact: DerivedArtifact
  onDismiss?: (artifact: DerivedArtifact) => void
}

export const DerivedArtifactRow = memo(function DerivedArtifactRowImpl({artifact, onDismiss}: DerivedArtifactRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  const handleCopyLink = useCallback(() => {
    void navigator.clipboard.writeText(artifact.url)
  }, [artifact.url])

  const handleDismiss = useCallback(() => {
    onDismiss?.(artifact)
  }, [artifact, onDismiss])

  let host = ''
  try {
    const parsed = new URL(artifact.url, window.location.origin)
    host = parsed.host.replace(/^www\./, '') + parsed.pathname.replace(/\/$/, '')
  } catch {
    host = artifact.url
  }

  return (
    <div className={clsx(styles.Row, menuOpen && styles.menuOpen)}>
      <div className={styles.RowContent}>
        <span className={styles.Icon}>
          <LinkExternalIcon size={14} />
        </span>
        <div className={styles.Content}>
          <Link className={styles.TitleLink} href={artifact.url} target="_blank" rel="noopener noreferrer">
            {artifact.title}
          </Link>
          <span className={styles.Description}>{host}</span>
        </div>
      </div>
      <div className={styles.HoverActions}>
        <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              size="small"
              aria-label="More actions"
              onClick={e => e.stopPropagation()}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.LinkItem href={artifact.url} target="_blank" rel="noopener noreferrer">
                <ActionList.LeadingVisual>
                  <LinkExternalIcon />
                </ActionList.LeadingVisual>
                Open link
              </ActionList.LinkItem>
              <ActionList.Item onSelect={handleCopyLink}>
                <ActionList.LeadingVisual>
                  <CopyIcon />
                </ActionList.LeadingVisual>
                Copy link
              </ActionList.Item>
              <ActionList.Divider />
              <ActionList.Item onSelect={handleDismiss}>
                <ActionList.LeadingVisual>
                  <EyeClosedIcon />
                </ActionList.LeadingVisual>
                Unlink from section
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
})
